import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { MusicSeparationController } from './useMusicSeparationController';

/**
 * The "Keep Original Music" choice on the dub popover, as the popover sees it.
 * Built by the HOST (see {@link useKeepOriginalMusic}) because the popover
 * closes the moment a run starts, while a separation it asked for may still be
 * running minutes later.
 */
export interface DubMusicOption {
  /** Starting position of the box. Ticked unless the option is unavailable. */
  defaultChecked: boolean;
  /** Why the box can't be ticked; `null` when it can. */
  unavailableReason: string | null;
  /** The music isn't separated yet, so ticking the box starts a separation. */
  needsSeparation: boolean;
  /** A separation is already running. */
  separating: boolean;
  /** Called once by Generate with the box's final value. */
  apply: (keep: boolean) => void;
}

/**
 * Owns the "Keep Original Music" choice for the dub popover.
 *
 * Separation is language-agnostic and reads only the source recording, so it
 * never waits on the dub: ticking the box on a story that isn't separated yet
 * starts the split alongside the dub run, and the two finish in either order.
 *
 * The music position can only be applied once the bed exists — on a timeline
 * surface, setting it with no bed would compose nothing, and the bed arriving
 * later does not recompose on its own. So the choice is held as an intent and
 * applied the moment the bed's URL lands; a split that fails or finds no music
 * drops it (the host's separation callbacks toast the outcome).
 *
 * Must be called AFTER the host's dub-composition hook, so the bed URL has
 * reached the editor's dub source before the music position is written.
 */
export function useKeepOriginalMusic({
  controller,
  durationSec,
  maxMinutes,
}: {
  controller: MusicSeparationController;
  /** Source recording length (seconds); separation is capped at `maxMinutes`. */
  durationSec: number | null | undefined;
  /** LaunchDarkly `stemSeparationMaxMinutes`. */
  maxMinutes: number;
}): DubMusicOption {
  const {
    status,
    hasStems,
    noMusic,
    bedUrl,
    musicMode,
    setMusicMode,
    separate,
  } = controller;
  const hasMusicBed = hasStems && !noMusic;
  const pendingRef = useRef(false);

  useEffect(() => {
    if (!pendingRef.current) return;
    if (status === 'error' || status === 'idle' || (hasStems && noMusic)) {
      // Failed, cancelled, or nothing to keep.
      pendingRef.current = false;
      return;
    }
    if (!bedUrl) return;
    pendingRef.current = false;
    setMusicMode('original');
  }, [status, hasStems, noMusic, bedUrl, setMusicMode]);

  const apply = useCallback(
    (keep: boolean) => {
      if (!keep) {
        pendingRef.current = false;
        if (hasMusicBed && musicMode !== 'none') setMusicMode('none');
        return;
      }
      if (hasMusicBed) {
        if (musicMode !== 'original') setMusicMode('original');
        return;
      }
      pendingRef.current = true;
      if (status !== 'running') separate();
    },
    [hasMusicBed, musicMode, setMusicMode, status, separate],
  );

  const tooLong = !!durationSec && durationSec > maxMinutes * 60;
  const unavailableReason =
    status === 'loading'
      ? 'Checking this story’s audio…'
      : hasStems && noMusic
        ? 'No music was found in the original recording.'
        : !hasMusicBed && tooLong
          ? `Music separation is available for tracks ${maxMinutes} minutes or less.`
          : null;

  return useMemo(
    () => ({
      // Ticked by default, even when that means starting a separation.
      defaultChecked: true,
      unavailableReason,
      needsSeparation: !hasMusicBed,
      separating: status === 'running',
      apply,
    }),
    [hasMusicBed, unavailableReason, status, apply],
  );
}
